import {
  RailConfigType,
  type SwappedFormOfPayment,
  getFops,
} from '@funkit/api-base'
import {
  DEFAULT_FIAT_CURRENCY,
  FOPS_STALE_TIME,
  type SwappedWithdrawalLegs,
  buildSwappedSellLimits,
  buildSwappedThirdPartyPaymentConfigs,
  findSwappedSourceOverride,
  getExchangeRate,
  getSwappedCashMinUsd,
  getSwappedSellCapEur,
  resolveSwappedWithdrawalLegs,
  sortFops,
  useSwappedDefaultCurrencyQuery,
} from '@funkit/connect-core'
import { useDynamicConfig } from '../../statsig/useDynamicConfig'
import { useTheme } from '@shopify/restyle'
import { useStatsigClient } from '@statsig/react-bindings'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type {
  WithdrawalFlowConfig,
  WithdrawalToken,
} from '../../flow/withdrawalFlowConfig'
import { useUserIpInfo } from '../../geo/useUserIpInfo'
import { useFunkitIdentity } from '../../identity/IdentityContext'
import { logger } from '../../logging/logger'
import { buildSwappedThemeQueryParamsRN } from '../../swapped/buildSwappedTheme'
import type { FunkitTheme } from '../../theme/toRestyleTheme'
import { excludeUnsupportedPlatformFops } from '../fops'
import { useFiatExchangeRates } from './useFiatExchangeRates'

/** Base react-query key for the withdrawal `/fops` query — shared with the
 * flow's invalidation so the two can't drift. */
export const WITHDRAW_FOPS_QUERY_KEY = 'withdrawFops'

export interface UseWithdrawalFopsParams {
  config: WithdrawalFlowConfig
  enabled?: boolean
}

export interface UseWithdrawalFopsResult {
  /**
   * Generic cash payout methods (Card / Bank Transfer / …). Saved FOPs are not
   * supported for withdrawals (web parity) and are never returned.
   */
  fops: SwappedFormOfPayment[]
  isLoading: boolean
  isError: boolean
  /**
   * True only once we KNOW there's nothing to show (gate off, or a settled
   * fetch returned zero) — never during a load, so the non-retryable
   * "unavailable" screen can't flash.
   */
  noPaymentMethodsAvailable: boolean
  /** Source balance can't clear Swapped's €7 minimum sell (cash impossible). */
  isBelowMinimumBalance: boolean
  refetch: () => void
  /** Whether the Statsig `enableswappedwithdrawal` gate is on for this apiKey. */
  isWithdrawalEnabled: boolean
  /** Resolved source→accepted-token route, shared by the fops fetch and quote. */
  route: SwappedWithdrawalLegs
}

// ── Step: resolve the withdrawal route ──────────────────────────────────────

/**
 * Resolve the on-chain route legs (source the user sells → the token Swapped
 * accepts), applying the Statsig `swappedwithdrawalsourceoverrides` rule.
 * Memoized so the returned object stays referentially stable for the downstream
 * execution `useCallback`.
 */
function useWithdrawalRoute(
  config: WithdrawalFlowConfig,
): SwappedWithdrawalLegs {
  const sourceOverrides = useDynamicConfig('swappedwithdrawalsourceoverrides')
  return useMemo(
    () =>
      resolveSwappedWithdrawalLegs({
        source: config.sourceToken,
        target: config.swappedAcceptedToken,
        override: findSwappedSourceOverride(sourceOverrides, {
          chainId: config.sourceToken.chainId,
          tokenAddress: config.sourceToken.address,
        }),
      }),
    [config.sourceToken, config.swappedAcceptedToken, sourceOverrides],
  )
}

// ── Step: source balance → EUR sell cap ─────────────────────────────────────

interface WithdrawalSellCap {
  /** Host balance in USD (0 when none is configured or it hasn't resolved). */
  usdBalance: number
  /** Floored EUR cap forwarded to Swapped, or undefined when uncapped. */
  eurMax: number | undefined
  /** Balance can't clear Swapped's EUR minimum → cash methods are impossible. */
  isBelowMinimumBalance: boolean
  /** A cap is still resolving — hold /fops so its first request isn't uncapped. */
  isAwaitingCap: boolean
}

/**
 * Resolve the host's USD balance and derive the EUR sell cap (web parity:
 * `WithdrawalMethodSelect`). A callback balance (e.g. an on-chain `balanceOf`)
 * is fetched via query; a plain number is used as-is.
 */
function useWithdrawalSellCap(
  config: WithdrawalFlowConfig,
  walletAddress: string,
): WithdrawalSellCap {
  const { sourceUsdBalance } = config
  const isBalanceCallback = typeof sourceUsdBalance === 'function'
  const hasBalanceInfo = sourceUsdBalance != null

  const balanceQuery = useQuery({
    queryKey: [
      'withdrawSourceUsdBalance',
      walletAddress,
      config.sourceToken.chainId,
      config.sourceToken.address,
    ] as const,
    queryFn: async () => {
      const value = isBalanceCallback ? await sourceUsdBalance() : 0
      return Number.isFinite(value) && value > 0 ? value : 0
    },
    enabled: isBalanceCallback && !!walletAddress && walletAddress !== '0x',
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const isLoadingBalance = isBalanceCallback && balanceQuery.isLoading
  // Sanitize both paths — the callback result is already clamped in its queryFn,
  // but a host could also pass a negative / NaN plain number.
  const rawUsdBalance = isBalanceCallback
    ? (balanceQuery.data ?? 0)
    : (sourceUsdBalance ?? 0)
  const usdBalance =
    Number.isFinite(rawUsdBalance) && rawUsdBalance > 0 ? rawUsdBalance : 0

  // markup 0 so the cap reflects the true balance (not an inflated +5%); floor
  // it so the sell never exceeds what the user holds and the fops query key
  // stays stable against sub-unit balance jitter (web parity).
  //
  // Enabled on `hasBalanceInfo` (not `usdBalance > 0`) so the keyless rates
  // table fetches in parallel with the balance callback instead of waiting for
  // it — `isAwaitingCap` still holds /fops until both resolve.
  const { data: exchangeRates, isLoading: isLoadingRates } =
    useFiatExchangeRates(hasBalanceInfo)
  const eurRate = getExchangeRate('EUR', exchangeRates, 0)
  const eurMax = getSwappedSellCapEur(usdBalance, eurRate)

  const cashMinUsd = getSwappedCashMinUsd(eurRate)
  const isBelowMinimumBalance =
    hasBalanceInfo &&
    !isLoadingBalance &&
    cashMinUsd != null &&
    usdBalance < cashMinUsd

  const isAwaitingCap =
    hasBalanceInfo && (isLoadingBalance || (usdBalance > 0 && isLoadingRates))

  return { usdBalance, eurMax, isBelowMinimumBalance, isAwaitingCap }
}

// ── Step: the /fops request ─────────────────────────────────────────────────

interface FetchWithdrawalFopsArgs {
  apiKey: string
  userId: string
  walletAddress: string
  countryCode: string
  swappedCurrencyCode: string
  fopPriority: Parameters<typeof sortFops>[1]
  themeConfig: ReturnType<typeof buildSwappedThemeQueryParamsRN>
  route: SwappedWithdrawalLegs
  eurMax: number | undefined
  usdBalance: number
  /** The user's raw source token — for the route-decision log only. */
  sourceToken: WithdrawalToken
  /** Host-supplied email prefilled into the Swapped payout widget, if any. */
  prefillFiatEmail: string | undefined
}

/**
 * Fetch + sort the Swapped `CRYPTO_TO_FIAT` payout methods for the accepted
 * token. The rail's `sourceToken` is the resolved ACCEPTED token (not the
 * user's raw source) — mirroring web, where the override/config target feeds
 * the `/fops` rail. `recipientAddress`/`destinationToken` are schema-required
 * but ignored for off-ramp; the wallet address fills the former.
 */
async function fetchWithdrawalFops({
  apiKey,
  userId,
  walletAddress,
  countryCode,
  swappedCurrencyCode,
  fopPriority,
  themeConfig,
  route,
  eurMax,
  usdBalance,
  sourceToken,
  prefillFiatEmail,
}: FetchWithdrawalFopsArgs): Promise<SwappedFormOfPayment[]> {
  const acceptedToken = {
    chainId: route.targetChainId,
    address: route.targetTokenAddress,
  }

  // Decision point: which accepted-token rail we request the methods for, and
  // why (Statsig source override vs configured/source token). Logged at fetch
  // time so sessions that list methods but never place an order still record
  // how the route was chosen (web parity: `withdraw-fops`).
  logger.info('connect-rn:withdrawFops:source_override', {
    overrideApplied: route.overrideApplied,
    sourceChainId: sourceToken.chainId,
    sourceTokenAddress: sourceToken.address,
    acceptedChainId: route.targetChainId,
    acceptedTokenAddress: route.targetTokenAddress,
    acceptedTokenSymbol: route.targetTokenSymbol,
  })

  // Decision point: whether the balance-derived EUR cap is forwarded as the
  // sell limit or suppressed (at/below the minimum — a contradictory range).
  const sellLimits = buildSwappedSellLimits(eurMax)
  logger.info('connect-rn:withdrawFops:sellLimits', {
    defaultMinAmount: sellLimits.defaultMinAmount,
    usdBalance,
    eurMax,
    capForwarded: sellLimits.defaultMaxAmount != null,
  })

  const response = await getFops({
    apiKey,
    logger,
    params: {
      recipientAddress: walletAddress,
      userId,
      destinationToken: acceptedToken,
      fiatCurrencyCode: swappedCurrencyCode,
      countryCode,
      railConfigs: [
        {
          type: RailConfigType.CRYPTO_TO_FIAT,
          userId,
          countryCode,
          fiatCurrencyCode: swappedCurrencyCode,
          sourceToken: acceptedToken,
          thirdPartyPaymentConfigs: buildSwappedThirdPartyPaymentConfigs({
            prefillFiatEmail,
            themeConfig,
            logTag: 'connect-rn:withdrawFops',
            logger,
          }),
          checkoutLimitsCriteria: sellLimits,
        },
      ],
    },
  })

  const fops = sortFops(response.genericFormsOfPayments, fopPriority)
  if (fops.length === 0) {
    logger.info('connect-rn:withdrawFops:empty', {
      countryCode,
      targetChainId: route.targetChainId,
      targetTokenAddress: route.targetTokenAddress,
    })
  }
  return fops
}

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * RN port of web's `useWithdrawFops`. Orchestrates the steps: resolve the
 * accepted-token legs, the region country/currency, the balance-derived sell
 * cap, then run the gated `/fops` query.
 */
export function useWithdrawalFops({
  config,
  enabled = true,
}: UseWithdrawalFopsParams): UseWithdrawalFopsResult {
  const { apiKey, userId, prefillFiatEmail } = useFunkitIdentity()
  // Statsig readiness: while it's loading, the gate reads its `false` fallback,
  // which is indistinguishable from a genuinely-off gate — treat init as
  // loading, not "unavailable".
  const { isLoading: isStatsigLoading } = useStatsigClient()
  const isWithdrawalEnabled = !!useDynamicConfig('enableswappedwithdrawal')
  const fopPriority = useDynamicConfig('foppriority')
  // Cash payout needs a signer to move funds to Swapped's deposit address, so a
  // walletless config yields no address and the existing `!!walletAddress` gate
  // below withholds the fiat methods rather than offering broken ones.
  const walletAddress = config.withdrawalWallet?.address() ?? ''

  const route = useWithdrawalRoute(config)

  // Country from the user's IP; no default (web parity). How an unresolved or
  // empty country is handled lives downstream: the fetch holds on it (isEnabled)
  // and a settled-but-empty lookup surfaces as an error (isError).
  const { userIpInfo, isLoadingGeoCheck } = useUserIpInfo()
  const countryCode = userIpInfo?.alpha2

  // Region-default fiat currency (web parity); falls back to USD. Gating /fops
  // on its loading state avoids an initial USD fetch that refetches once the
  // region currency resolves.
  const {
    data: swappedCurrencyCode = DEFAULT_FIAT_CURRENCY,
    isLoading: isSwappedCurrencyCodeLoading,
  } = useSwappedDefaultCurrencyQuery({
    apiKey,
    countryCode: userIpInfo?.alpha2,
    logger,
  })

  // Loading-state theme baked into the returned embeddedFlowUrls (web parity).
  const theme = useTheme<FunkitTheme>()
  const themeConfig = useMemo(
    () => buildSwappedThemeQueryParamsRN(theme),
    [theme],
  )

  const { usdBalance, eurMax, isBelowMinimumBalance, isAwaitingCap } =
    useWithdrawalSellCap(config, walletAddress)

  const isEnabled =
    enabled &&
    isWithdrawalEnabled &&
    !!apiKey &&
    !!userId &&
    !!walletAddress &&
    walletAddress !== '0x' &&
    !!countryCode &&
    !isSwappedCurrencyCodeLoading &&
    !isAwaitingCap

  const query = useQuery({
    // `prefillFiatEmail` is intentionally NOT in the queryKey (web parity): it's
    // read at fetch time, so post-mount changes don't trigger a refetch.
    queryKey: [
      WITHDRAW_FOPS_QUERY_KEY,
      userId,
      walletAddress,
      apiKey,
      countryCode,
      swappedCurrencyCode,
      fopPriority,
      themeConfig,
      route.targetChainId,
      route.targetTokenAddress,
      eurMax,
    ] as const,
    // `countryCode` is guaranteed by `isEnabled` (query stays disabled until it
    // resolves); the guard just narrows the type — the empty fallback is unreachable.
    queryFn: () =>
      countryCode
        ? fetchWithdrawalFops({
            apiKey,
            userId,
            walletAddress,
            countryCode,
            swappedCurrencyCode,
            fopPriority,
            themeConfig,
            route,
            eurMax,
            usdBalance,
            sourceToken: config.sourceToken,
            prefillFiatEmail,
          })
        : Promise.resolve([]),
    enabled: isEnabled,
    staleTime: FOPS_STALE_TIME,
    refetchOnWindowFocus: false,
  })

  // Loading spans every window where the answer isn't knowable yet: Statsig
  // init, the geo lookup, the region-currency lookup, the balance/rate cap
  // resolution, and the enabled fops fetch.
  const isLoading =
    isStatsigLoading ||
    isLoadingGeoCheck ||
    isSwappedCurrencyCodeLoading ||
    isAwaitingCap ||
    (isEnabled && query.isLoading)

  // Known-empty only once we actually know: gate confirmed off (Statsig
  // settled) or a settled fetch that returned nothing. Never during a load.
  const noPaymentMethodsAvailable =
    !isLoading &&
    (!isWithdrawalEnabled ||
      (query.isSuccess && (query.data?.length ?? 0) === 0))

  return {
    fops: query.data ?? [],
    isLoading,
    noPaymentMethodsAvailable,
    isBelowMinimumBalance,
    // Tie the query error to isEnabled ("gate off ⇒ no error"), plus web's
    // no-country case: geo settled without a country is unrecoverable here.
    isError:
      (isEnabled && query.isError) || (!isLoadingGeoCheck && !countryCode),
    // Respect the enabled gate — refetch() would otherwise force a fetch even
    // when disabled (e.g. no resolved country).
    refetch: () => {
      if (isEnabled) {
        void query.refetch()
      }
    },
    isWithdrawalEnabled,
    route,
  }
}
